import { useCallback, useContext, useMemo } from 'react';
import { Info } from 'react-feather';
import styled from '@emotion/styled';
import { Box } from '@mui/material';
import { ChatContextType } from '@trinity/chat';
import {
  LinkContext,
  SidePanelExpandedContext,
} from '@trinity/chat-components/components/BrandInsights/context';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { PageRenderer } from '@trinity/chat-components/components/PageDashboard/PageRenderer/index.js';
import AudioPlayerController from '@trinity/chat-components/components/TextToSpeechAudioPlayer/AudioPlayerController.js';
import { Constants } from '@trinity/chat-components/components/Theme';
import { RepDashboardProfileClickContext } from '@trinity/chat-components/components/Widgets/RepDashboardProfileClickContext';
import { RepDashboardSubmitQueryContext } from '@trinity/chat-components/components/Widgets/RepDashboardSubmitQueryContext';
import { BrandingProvider } from '@trinity/chat-components/containers/Branding';
import { GroupFeaturesContext } from '@trinity/chat-components/containers/Chat';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { AudioControls } from '@trinity/chat-components/containers/TextToSpeechAudioPlayer/AudioControls';
import { TextToSpeechStateContext } from '@trinity/chat-components/containers/TextToSpeechAudioPlayer/Context.js';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { useModal } from '@trinity/chat-components/hooks/useModal';
import {
  ActivePageContext,
  MessagesListRefContext,
  ChatHasAccessContext,
  ChatSessionItemsContext,
  AuthorContext,
  ChatLandingCardsVisibleContext,
  ActiveChatSessionContext,
} from './context';
import { ChatGuidelinesModal } from './GuidelinesModal';
import { useChatGuidelinesCookie } from './hooks/useChatGuidelinesCookie';
import { useChatPermissions } from './hooks/useChatPermissions';
import { UnsubmittedMessage } from './Input';
import { LandingCards } from './LandingCards';
import { messageLaneWidth } from './Message.styles';
import { InitialSystemMessage } from './Message.System.Initial';
import { MessageList } from './MessageList';
import { NoAccessBanner } from './NoAccessBanner';

type Props = {
  className?: string;
  isSidePanel?: boolean;
};

export const ChatInstance = (props: Props) => {
  const hasAccess = useContext(ChatHasAccessContext);
  const link = useContext(LinkContext);
  const chatPermissions = useChatPermissions();

  const [_, setMessagesRef] = useContext(MessagesListRefContext);
  const author = useContext(AuthorContext);
  const isFirstSession = author.isFirstSession;
  const { isDismissed: chatGuidelinesDismissed } = useChatGuidelinesCookie();
  const [toggleGuidelinesModal, GuidelinesModal] = useModal(ChatGuidelinesModal, {
    defaultOpen: isFirstSession && !chatGuidelinesDismissed,
  });
  const features = useContext(GroupFeaturesContext);
  const chatGuidelines = features?.chatGuidelines;
  const showChatGuidelines = !!chatGuidelines;
  const disableChatInputWithLandingCards = features?.disableChatInputWithLandingCards;

  const items = useContext(ChatSessionItemsContext);

  const showLandingCards = useContext(ChatLandingCardsVisibleContext);

  const { pageId: activePageId, clearPage } = useContext(ActivePageContext);
  const apiClient = useChatApiClient();
  const chat = useContext(ActiveChatSessionContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const brandIdentifier = useActiveBrandIdentifier();
  const proxyFetch = useCallback(
    ({ agentId, extensionPath }: { agentId: number; extensionPath: string }) =>
      apiClient.pages.proxyWidgetData(agentId, extensionPath, brandIdentifier ?? undefined),
    [apiClient, brandIdentifier]
  );
  const [isSidePanelExpanded] = useContext(SidePanelExpandedContext);

  const handleRepProfileClick = useCallback(
    (profileId: string, name: string) => {
      clearPage();

      if (chat.context.type !== ChatContextType.Agent) {
        return;
      }

      if (!brandIdentifier) {
        return;
      }

      dispatch({
        type: 'new-chat-started',
        payload: {
          brandIdentifier,
          context: {
            ...chat.context,
            data: {
              ...chat.context.data,
              subject: {
                id: profileId,
                name,
              },
            },
          },
          pendingQuery: `Show me more about ${name}`,
        },
      });
    },
    [clearPage, chat.context, brandIdentifier, dispatch]
  );

  const handleRepSubmitQuery = useCallback(
    (query: string) => {
      clearPage();

      if (chat.context.type !== ChatContextType.Agent) {
        return;
      }

      if (!brandIdentifier) {
        return;
      }

      dispatch({
        type: 'new-chat-started',
        payload: {
          brandIdentifier,
          context: chat.context,
          pendingQuery: query,
        },
      });
    },
    [clearPage, chat.context, brandIdentifier, dispatch]
  );

  const renderMessageList = useCallback(() => {
    if (!link.initialized) return null;

    if (showLandingCards) {
      return <LandingCards />;
    }

    if (!items.length) {
      return <InitialSystemMessage />;
    }

    return <MessageList items={items} />;
  }, [items, link.initialized, showLandingCards]);

  const showAudioPlayer = useShowAudioPlayer();

  const renderAudioPlayer = useCallback(() => {
    if (!showAudioPlayer) return null;

    return (
      <AudioPlayerRow>
        <AudioPlayerBox>
          <AudioPlayerController>
            {({
              duration,
              position,
              isLoading,
              onScan,
              onSeek,
              onStop,
              onPreviousTrack,
              onNextTrack,
              onSkipBackward,
              onSkipAhead,
              playing,
              onTogglePlay,
              canSkipBackward,
              canSkipForward,
            }) => (
              <AudioControls
                isPlaying={playing}
                isLoading={isLoading}
                onTogglePlay={onTogglePlay}
                onPreviousTrack={onPreviousTrack}
                onNextTrack={onNextTrack}
                onSkipBackward={onSkipBackward}
                onSkipAhead={onSkipAhead}
                disabledPreviousTrack={!canSkipBackward}
                disabledNextTrack={!canSkipForward}
                onStop={onStop}
                currentTime={position}
                duration={duration}
                onScan={onScan}
                onSeek={onSeek}
              />
            )}
          </AudioPlayerController>
        </AudioPlayerBox>
      </AudioPlayerRow>
    );
  }, [showAudioPlayer]);

  const PageRendererRoot = props.isSidePanel ? SidePanelPageRenderer : FullPageRenderer;

  return (
    <BrandingProvider>
      <Root className={props.className}>
        {!hasAccess && <NoAccessBanner />}

        {((!props.isSidePanel && isSidePanelExpanded) || activePageId != null) && (
          <PageArea>
            <RepDashboardProfileClickContext.Provider value={handleRepProfileClick}>
              <RepDashboardSubmitQueryContext.Provider value={handleRepSubmitQuery}>
                <PageRendererRoot>
                  <PageRenderer
                    pageId={activePageId}
                    proxyFetch={proxyFetch}
                    brandIdentifier={brandIdentifier ?? undefined}
                  />
                </PageRendererRoot>
              </RepDashboardSubmitQueryContext.Provider>
            </RepDashboardProfileClickContext.Provider>
          </PageArea>
        )}
        {((props.isSidePanel && isSidePanelExpanded) || activePageId == null) && (
          <Messages>
            <MessagesWrap ref={setMessagesRef}>{renderMessageList()}</MessagesWrap>
          </Messages>
        )}

        <Footer>
          {renderAudioPlayer()}
          {!(!props.isSidePanel && isSidePanelExpanded) &&
            chatPermissions.canModify &&
            !(disableChatInputWithLandingCards && showLandingCards) && <UnsubmittedMessage />}
          {showChatGuidelines && (
            <DisclaimerContainer>
              <DisclaimerCopy>
                Shared content is meant for internal use only and not meant to be shared externally.
              </DisclaimerCopy>
              <OpenDisclaimer onClick={toggleGuidelinesModal}>
                View Usage Guidelines <Info size={16} />
              </OpenDisclaimer>
            </DisclaimerContainer>
          )}
        </Footer>

        {showChatGuidelines && <GuidelinesModal guidelines={chatGuidelines} />}
      </Root>
    </BrandingProvider>
  );
};

function useShowAudioPlayer() {
  const features = useContext(GroupFeaturesContext);
  const audioPlayerState = useContext(TextToSpeechStateContext);
  const audioPlayerEnabled = features?.chatReadAloud && features?.chatVoiceSource !== 'browser';

  return useMemo(
    () => audioPlayerEnabled && (!!audioPlayerState.queryId || !!audioPlayerState.kolSearchId),
    [audioPlayerEnabled, audioPlayerState]
  );
}

const MaxContainerWidth = 1100;

const DisclaimerContainer = styled.div({
  width: messageLaneWidth,
  margin: '0 auto',
  display: 'flex',
  justifyContent: 'center',
  flexWrap: 'wrap',
  containerType: 'inline-size',
});

const DisclaimerCopy = styled.span({
  textAlign: 'center',
  '@container (max-width: 640px)': {
    display: 'none',
  },
});

const OpenDisclaimer = styled.div(({ theme }) => ({
  display: 'inline-flex',
  color: theme.palette.sentiment.primary.main,
  textDecoration: 'underline',
  cursor: 'pointer',
  marginLeft: 10,
  alignItems: 'center',
  gap: 4,
  '@container (max-width: 640px)': {
    marginLeft: 0,
    marginTop: 4,
  },
}));

const Root = styled.div({
  display: 'flex',
  flexDirection: 'column',
  height: `calc(100% - ${Constants.Modal.HeaderHeight}px)`,
  width: '100%',
  margin: '0 auto',
  boxSizing: 'border-box',
});

const Messages = styled.div({
  boxSizing: 'border-box',
  height: '100%',
  overflowY: 'auto',
  overflowX: 'hidden',
  backgroundRepeat: 'no-repeat',
  backgroundSize: '100% 40px, 100% 40px, 100% 14px, 100% 14px',
  backgroundAttachment: 'local, local, scroll, scroll',
});

const PageArea = styled.div({
  boxSizing: 'border-box',
  flex: 1,
  minHeight: 0,
  height: '100%',
  overflow: 'hidden',
});

const MessagesWrap = styled.div({
  minHeight: '100%',
  width: '100%',
  maxWidth: MaxContainerWidth,
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  gap: 15,
  paddingTop: 15,
  paddingRight: 10,
  paddingLeft: 10,
  boxSizing: 'border-box',
});

const Footer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  padding: `20px 10px ${15}px 10px`,
  position: 'relative',
  boxSizing: 'border-box',
  width: '100%',
  maxWidth: MaxContainerWidth,
  margin: '0 auto',
});

const AudioPlayerRow = styled.div({
  display: 'flex',
  justifyContent: 'flex-end',
  position: 'absolute',
  right: 9,
  bottom: `calc(100% - 8px)`,
});

const AudioPlayerBox = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  backgroundColor: theme.palette.white.main,
  borderRadius: 5,
  border: `2px solid ${theme.palette.gray.light2}`,
  padding: 10,
  width: 'auto',
  '&:hover': {
    border: `2px solid ${theme.palette.primary.main}`,
  },
}));

const FullPageRenderer = styled(Box)(({ theme }) => ({
  width: '85%',
  maxWidth: '85%',
  height: 'calc(100% - 24px)',
  margin: '0 auto',
  marginTop: '24px',
  backgroundColor: theme.palette.background.paper,
  borderRadius: 5,
  border: `2px solid ${theme.palette.gray.light2}`,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
}));

const SidePanelPageRenderer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  height: '100%',
  backgroundColor: theme.palette.background.paper,
  borderBottom: `2px solid ${theme.palette.gray.light2}`,
  borderTop: `2px solid ${theme.palette.gray.light2}`,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
}));

export default ChatInstance;
